import { FormInputProps } from '@/@types/form.types';
import { cn } from '@/lib/utils';
import { Controller } from 'react-hook-form';
import InformationCircle from '@/components/icons/InformationCircle';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuCheckboxItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';

type Props<T extends {}> = {
	label?: string;
	list: string[];
	optional?: boolean;
	selectVariant?: 'single' | 'multi';
	buttonClassName?: string;
} & FormInputProps<T>;

const EListPopupPicker = <T extends {}>({
	label,
	placeholder = 'Select from list',
	list,
	optional = false,
	fieldName,
	control,
	containerClassName,
	buttonClassName,
	disabled,
	selectVariant = 'single',
}: Props<T>) => {
	const [isOpen, setIsOpen] = useState(false);

	const isOpenToggler = () => {
		setIsOpen((prev) => !prev);
	};

	return (
		<div
			className={cn(
				'w-full flex flex-col gap-1.5 select-none',
				containerClassName,
			)}>
			{label && (
				<h4 className='text-sm font-medium'>
					{label}
					{optional && (
						<span className='text-popover-600'> (optional)</span>
					)}
				</h4>
			)}
			<Controller
				name={fieldName}
				control={control}
				render={({
					field: { value, onChange, onBlur },
					fieldState,
					formState,
				}) => {
					const isMulti = selectVariant === 'multi';

					const selectedValues = Array.isArray(value)
						? (value as string[])
						: typeof value === 'string'
						? [value]
						: [];

					const toggleValue = (tag: string) => {
						if (isMulti) {
							if (selectedValues.includes(tag)) {
								onChange(
									selectedValues.filter(
										(value) => value !== tag,
									),
								);
							} else {
								onChange([...selectedValues, tag]);
							}
						} else {
							onChange(tag);
							isOpenToggler();
						}
					};

					return (
						<DropdownMenu
							open={isOpen}
							onOpenChange={isOpenToggler}>
							<DropdownMenuTrigger
								asChild
								className={cn(
									'w-full px-4 py-3 flex rounded-semi-xl',
									fieldState.invalid
										? 'border-error-1000'
										: '',
									isOpen ? 'border-primary-color-1000' : '',
								)}>
								<Button
									variant='outline'
									disabled={disabled || formState.disabled}
									className={buttonClassName}>
									<span className='text-xs text-popover-1000 truncate'>
										{!!selectedValues.length
											? selectedValues.join(', ')
											: placeholder}
									</span>
									<div
										className={cn(
											'transition-transform duration-50 ease-in-out ml-auto',
											isOpen ? '-rotate-180' : 'rotate-0',
										)}>
										{<ArrowDownIcon />}
									</div>
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								className={
									'w-(--radix-dropdown-menu-trigger-width) h-fit max-h-50 p-0 rounded-semi-xl border border-popover-100 bg-popover-10 overflow-y-scroll no-scrollbar'
								}>
								{list.map((name) => (
									<DropdownMenuCheckboxItem
										key={name}
										className='px-3.5 py-3'
										checked={selectedValues.includes(name)}
										onSelect={(e) => e.preventDefault()}
										onCheckedChange={() =>
											toggleValue(name)
										}
										onBlur={onBlur}>
										{name}
									</DropdownMenuCheckboxItem>
								))}
							</DropdownMenuContent>
							{fieldState.invalid && (
								<div className='flex items-center gap-1.5'>
									<InformationCircle className='size-4 stroke-error-1000' />
									<span className='text-xs text-error-1000'>
										{fieldState.error?.message}
									</span>
								</div>
							)}
						</DropdownMenu>
					);
				}}
			/>
		</div>
	);
};

export default EListPopupPicker;
